import { NavLink, Outlet, Link } from "react-router-dom";
import { UtensilsCrossed, ShoppingBag, Wallet, ArrowLeftRight, Package, User, Bike, Briefcase, Zap, LifeBuoy, FileText, Car, MessageCircle, Gamepad2, Trophy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fmtUsdt, fmtCup } from "@/lib/api";
import { NotificationBell } from "@/components/NotificationBell";

const NAV = [
  { to: "/", label: "Comida", icon: UtensilsCrossed, id: "nav-food" },
  { to: "/ventas", label: "Ventas", icon: ShoppingBag, id: "nav-market" },
  { to: "/wallet", label: "Wallet", icon: Wallet, id: "nav-wallet" },
  { to: "/p2p", label: "P2P", icon: ArrowLeftRight, id: "nav-p2p" },
  { to: "/pedidos", label: "Pedidos", icon: Package, id: "nav-orders" },
  { to: "/perfil", label: "Perfil", icon: User, id: "nav-profile" },
];

export default function Layout() {
  const { user, settings } = useAuth();
  const isDriver = user?.driver_status === "approved";
  const isStaff = user?.role === "admin" || user?.role === "staff";
  const extra = [
    { to: "/juegos", label: "Área de Juego", icon: Gamepad2, id: "nav-games" },
    { to: "/predicciones", label: "Predicciones", icon: Trophy, id: "nav-predictions" },
    { to: "/uber", label: "Uber", icon: Car, id: "nav-uber" },
    { to: "/mensajes", label: "Mensajes", icon: MessageCircle, id: "nav-messages" },
    ...(isDriver ? [{ to: "/chofer", label: "Panel Chofer", icon: Bike, id: "nav-driver" }] : []),
    ...(isStaff ? [{ to: "/admin", label: "Trabajo / Admin", icon: Briefcase, id: "nav-admin" }] : []),
    { to: "/soporte", label: "Soporte", icon: LifeBuoy, id: "nav-support" },
    { to: "/terminos", label: "Términos", icon: FileText, id: "nav-terms" },
  ];
  return (
    <div className="min-h-screen lg:flex">
      <aside className="hidden lg:flex flex-col w-60 shrink-0 border-r border-border/60 bg-card/60 backdrop-blur-xl p-4 sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-2 px-2 py-3 mb-4" data-testid="logo-link">
          <span className="h-9 w-9 rounded-xl bg-primary/20 text-primary flex items-center justify-center glow-cyan"><Zap size={18} /></span>
          <span className="font-display font-bold text-lg">{settings.platform_name || "CubiMarket"}</span>
        </Link>
        <nav className="flex flex-col gap-1">
          {[...NAV, ...extra].map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.id} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
              <n.icon size={18} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto space-y-3">
        <div className="flex items-center justify-between px-1"><span className="text-xs text-muted-foreground">Notificaciones</span><NotificationBell /></div>
        <div className="neon-card p-3">
          <div className="text-xs text-muted-foreground">Saldo disponible</div>
          <div className="font-mono font-bold text-cyan-300 text-lg" data-testid="sidebar-balance">{fmtUsdt(user?.balance)} USDT</div>
          <div className="font-mono text-sm text-emerald-400" data-testid="sidebar-balance-cup">{fmtCup(user?.balance_cup)} CUP</div>
        </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between gap-2 px-4 py-3 bg-background/80 backdrop-blur-xl border-b border-border/60">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="mobile-logo">
            <span className="h-8 w-8 rounded-lg bg-primary/20 text-primary flex items-center justify-center"><Zap size={16} /></span>
            <span className="font-display font-bold hidden min-[480px]:inline">{settings.platform_name || "CubiMarket"}</span>
          </Link>
          <div className="flex items-center gap-1 min-w-0 overflow-x-auto no-scrollbar">
            <NotificationBell />
            <Link to="/juegos" data-testid="mobile-nav-games" className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center text-rose-300"><Gamepad2 size={16} /></Link>
            <Link to="/predicciones" data-testid="mobile-nav-predictions" className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center text-[#FFD700]"><Trophy size={16} /></Link>
            <Link to="/mensajes" data-testid="mobile-nav-messages" className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center text-foreground"><MessageCircle size={16} /></Link>
            <Link to="/uber" data-testid="mobile-nav-uber" className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center text-amber-300"><Car size={16} /></Link>
            {isDriver && <Link to="/chofer" data-testid="mobile-nav-driver" className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center text-emerald-300"><Bike size={16} /></Link>}
            {isStaff && <Link to="/admin" data-testid="mobile-nav-admin" className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center text-cyan-300"><Briefcase size={16} /></Link>}
            <Link to="/wallet" className="badge-usdt !py-1.5 !px-2.5 shrink-0 whitespace-nowrap" data-testid="header-balance">{fmtUsdt(user?.balance)} USDT</Link>
          </div>
        </header>

        {user?.must_change_password && (
          <Link to="/perfil" data-testid="change-password-banner" className="block bg-amber-500/15 text-amber-200 text-sm text-center py-2 px-4 border-b border-amber-500/30">
            Por seguridad, cambia tu contraseña inicial en tu Perfil →
          </Link>
        )}

        <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-5 pb-28 lg:pb-8">
          <Outlet />
        </main>

        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-card/90 backdrop-blur-xl border-t border-border/60 grid grid-cols-6 px-1 pb-[env(safe-area-inset-bottom)]">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`${n.id}-mobile`}
              className={({ isActive }) => `flex flex-col items-center justify-center gap-1 py-2.5 min-h-[60px] text-[11px] font-medium transition-colors ${isActive ? "text-primary" : "text-muted-foreground"}`}>
              <n.icon size={22} /> {n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
